import { messages } from '@databases/intl/messages';
import { currentSnackbarState } from '@databases/snackbar/snackbar';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { isOnline } from '@canvas/network';
import { RefreshBundlePoller } from '@canvas/refresh-bundle-poller';
import { retryWithBackoff, RecoverableNetworkError } from '@canvas/retry';

const manifestSupplier = () =>
  retryWithBackoff({
    maxRetries: 3,
    callback: async () => {
      try {
        const res = await fetch('/databases/assets/v/manifest.json', {
          cache: 'no-store',
        });

        if (!res.ok) {
          throw new RecoverableNetworkError(
            `Unable to check bundle version. Status: ${res.status}`
          );
        }

        return res.json();
      } catch (e) {
        throw new RecoverableNetworkError(
          `Unable to check bundle version. Network failure.`
        );
      }
    },
    shouldRetry: async (err) =>
      err instanceof RecoverableNetworkError && (await isOnline()),
  });

/**
 * Show a snackbar warning when an un-supported version is of databases client
 * is detected. This warning cannot be closed and shows a link to refresh the
 * users browser tab.
 */
export const useSupportedVersionWarning = () => {
  const { formatMessage } = useIntl();
  const [isStale, setIsStale] = useState(false);

  const onStaleBundle = useCallback(() => {
    setIsStale(true);
  }, []);

  const refreshBundlePoller = useMemo(
    () =>
      new RefreshBundlePoller({
        onStaleBundle,
        manifestSupplier,
      }),
    [onStaleBundle]
  );

  useEffect(() => {
    refreshBundlePoller.start();
    return () => {
      refreshBundlePoller.stop();
    };
  }, [refreshBundlePoller]);

  useEffect(() => {
    if (isStale) {
      currentSnackbarState.setMessage(
        formatMessage(messages['snackbar.version-unsupported']),
        {
          showCloseButton: false,
          messageData: [
            {
              content: formatMessage(messages['snackbar.link.refresh']),
              onClick: () => {
                window.location.reload();
              },
            },
          ],
        }
      );
      refreshBundlePoller.stop();
    }
  }, [formatMessage, isStale, refreshBundlePoller]);
};
